import React from "react";
import { Box, Typography, Button, Sheet } from "@mui/joy";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FaBaby, FaRegSmileBeam, FaLink, FaGift } from "react-icons/fa";
import DemoReveal from "../components/DemoReveal";
import { useTranslation, Trans } from "react-i18next"; // Dodane Trans
import SeoHelmet from "../components/SeoHelmet";

const pastelWave = (
  <motion.div
    initial={{ backgroundPosition: "0% 50%" }}
    animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
    transition={{
      duration: 12,
      repeat: Infinity,
      ease: "easeInOut"
    }}
    style={{
      position: "fixed",
      left: 0,
      right: 0,
      bottom: 0,
      height: 220,
      width: "100vw",
      zIndex: 1,
      pointerEvents: "none",
      background:
        "linear-gradient(90deg, #fce4ec 0%, #f8bbd0 25%, #d0eaff 75%, #bbdefb 100%)",
      backgroundSize: "200% 200%",
      opacity: 0.77,
      filter: "blur(28px)"
    }}
  />
);

const Feature = ({ icon, label, desc, color }) => (
  <Box
    sx={{
      background: "#fff",
      borderRadius: "18px",
      p: 2.7,
      boxShadow: "0 4px 32px #e3eaff38",
      textAlign: "center",
      minWidth: { xs: 140, md: 180 },
      mb: { xs: 2, md: 0 },
      mx: { xs: 0.5, md: 2 },
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "flex-start"
    }}
  >
    <Box sx={{ fontSize: 40, color: color, mb: 1 }}>{icon}</Box>
    <Typography sx={{ fontWeight: 700, mb: 0.5, fontSize: "1.07rem" }}>
      {label}
    </Typography>
    <Typography level="body-sm" sx={{ color: "#5e79b8", fontSize: "0.97rem" }}>
      {desc}
    </Typography>
  </Box>
);

const Home = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        background: "linear-gradient(to bottom right, #fce4ec 0%, #d0eaff 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        pb: 2,
        pt: { xs: 7, md: 10 },
        position: "relative",
        overflow: "hidden"
      }}
    >
            <SeoHelmet
        title={t("meta.homeTitle")}
        description={t("meta.homeDesc")}
      />
      {pastelWave}

      {/* HERO */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        style={{
          maxWidth: 720,
          width: "100%",
          zIndex: 2,
          margin: "0 auto",
          textAlign: "center"
        }}
      >
        <Typography
          level="h1"
          sx={{
            color: "#2d3c88",
            fontWeight: 800,
            fontSize: { xs: "2rem", md: "2.7rem" },
            mb: 2,
            letterSpacing: "0.02em"
          }}
        >
          <Trans i18nKey="hero.title" components={{ b: <b /> }} />
        </Typography>
        <Typography
          level="body-lg"
          sx={{
            color: "#354574",
            mb: 3.5,
            fontSize: { xs: "1.09rem", md: "1.24rem" },
            lineHeight: 1.5,
            fontWeight: 400
          }}
        >
                    <Trans
  i18nKey="hero.description"
  components={{
    b: <b />,
    br: <br />,
    blue: <span className="blue" />,
    pink: <span className="pink" />,
    violet: <span className="violet" />
  }}
/>
        </Typography>

        <motion.div
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.7 }}
        >
          {/* DEMO */}
          <Box sx={{ width: "100%", maxWidth: 580, mx: "auto", mt: 6, mb: 6 }}>
            <DemoReveal />
          </Box>
          <Button
            size="lg"
            color="primary"
            onClick={() => navigate(`/${i18n.language}/create`)}
            sx={{
              borderRadius: "18px",
              fontWeight: 700,
              fontSize: "1.1rem",
              px: 4,
              py: 1.5,
              boxShadow: "0 3px 20px #c3e2ff52",
              mb: 1.7
            }}
          >
            {t("hero.cta")}
          </Button>
        </motion.div>
      </motion.div>

      {/* BALONY */}
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7, duration: 1 }}
        style={{
          margin: "2.6rem 0 0.2rem 0",
          zIndex: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span style={{ fontSize: 44, margin: "0 6px" }}>🎈</span>
        <span style={{ fontSize: 44, margin: "0 6px" }}>💙</span>
        <span style={{ fontSize: 44, margin: "0 6px" }}>💗</span>
        <span style={{ fontSize: 44, margin: "0 6px" }}>🎈</span>
      </motion.div>

      {/* JAK TO DZIAŁA */}
      <Sheet
        sx={{
          background: "rgba(255,255,255,0.88)",
          borderRadius: "22px",
          p: { xs: 2.2, md: 4 },
          mt: { xs: 4, md: 6 },
          boxShadow: "0 8px 48px #c5d6ed44",
          maxWidth: 830,
          mx: "auto",
          width: "100%",
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
        variant="outlined"
      >
        <Typography
          level="h2"
          sx={{
            color: "#2d3c88",
            mb: 3,
            fontWeight: 800,
            fontSize: { xs: "1.38rem", md: "1.68rem" },
            textAlign: "center"
          }}
        >
          {t("how.title")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "center",
            alignItems: "stretch",
            gap: { xs: 2, md: 3 },
            width: "100%"
          }}
        >
          <Feature icon={<FaBaby />} color="#2196f3" label={t("how.step1.title")} desc={t("how.step1.desc")} />
          <Feature icon={<FaLink />} color="#fa5fa2" label={t("how.step2.title")} desc={t("how.step2.desc")} />
          <Feature icon={<FaRegSmileBeam />} color="#ec407a" label={t("how.step3.title")} desc={t("how.step3.desc")} />
          <Feature icon={<FaGift />} color="#b388ff" label={t("how.step4.title")} desc={t("how.step4.desc")} />
        </Box>
      </Sheet>
    </Box>
  );
};

export default Home;
